import { useState, useEffect, useLayoutEffect, useCallback, useRef, Fragment } from 'react'
import { flushSync } from 'react-dom'
import { Button } from '@/components/ui/button'
import { GripVertical, GripHorizontal } from 'lucide-react'
import styles from './fields.module.css'
import { createGeneralApi } from '../tbsGeneralApi'
import { resolveLayout } from '../ScreenGenerator'
import { cn, toCssLength, toStyleObject, safeInvoke } from '@/lib/utils'

// Splitter (attribut `splitter`, type 1 uniquement — type 20 non concerné) — panneaux
// redimensionnables entre les éléments directs du canvas, en remplacement du flux vertical simple/
// `layout` grid (mutuellement exclusif, voir le console.error dans CanvasField). `"vertical"` :
// barre de séparation verticale → panneaux côte à côte (`flex-direction: row`, poignée
// `col-resize`). `"horizontal"` : barre horizontale → panneaux empilés (`flex-direction: column`,
// poignée `row-resize`).
const SPLITTER_MIN_SIZE = 50 // px — aucun attribut JSON dédié (voir échanges avant implémentation)
const SPLITTER_BAR_SIZE = 10 // px — élargie (6→10) pour l'effet 3D (biseau + grip, voir plus bas)

function SplitterPanes({ elements, splitter, splitterSizes, onRenderFields }) {
  const isRow = splitter === 'vertical'
  const paneRefs = useRef([])
  // Tailles initiales : `splitterSizes[i]` (unité quelconque, toCssLength() — nombre/chaîne sans
  // unité → px) si fourni et de la bonne longueur, sinon répartition égale simple. Une fois le
  // drag démarré, tout bascule en px (mesure réelle du panneau, voir handleMouseDown) — l'unité
  // d'origine ne sert qu'au posé initial, cohérent avec une interaction souris intrinsèquement en
  // pixels.
  const [sizes, setSizes] = useState(() => {
    const n = elements.length
    if (Array.isArray(splitterSizes) && splitterSizes.length === n) {
      return splitterSizes.map(s => toCssLength(s))
    }
    return elements.map(() => `${100 / n}%`)
  })
  const sizesRef = useRef(sizes)
  useLayoutEffect(() => { sizesRef.current = sizes })
  const dragRef = useRef(null)

  // Listeners de la frappe en cours (mousemove/mouseup), pour le nettoyage au démontage ci-dessous —
  // pas de useCallback ici : move/up sont recréées à chaque mousedown (comme DraggableShell,
  // src/components/ui/draggable-shell.jsx/makeDrag) plutôt que mémoïsées, pour éviter qu'un handler
  // ait besoin de se référencer lui-même dans son propre corps pour s'auto-retirer (auto-référence
  // fragile sous react-hooks/immutability — un useCallback ne peut pas se lire fiablement lui-même
  // avant d'être pleinement assigné).
  const activeHandlersRef = useRef(null)

  const handleMouseDown = useCallback((index, e) => {
    e.preventDefault()
    const paneA = paneRefs.current[index]
    const paneB = paneRefs.current[index + 1]
    if (!paneA || !paneB) return
    const rectA = paneA.getBoundingClientRect()
    const rectB = paneB.getBoundingClientRect()
    dragRef.current = {
      index,
      startPos: isRow ? e.clientX : e.clientY,
      sizeA: isRow ? rectA.width : rectA.height,
      sizeB: isRow ? rectB.width : rectB.height,
    }
    const move = ev => {
      const drag = dragRef.current
      if (!drag) return
      const pos = isRow ? ev.clientX : ev.clientY
      let delta = pos - drag.startPos
      // Seuls les 2 panneaux voisins de la barre déplacée sont affectés (taille totale conservée,
      // simplement redistribuée entre eux) — garantit mécaniquement que la barre ne dépasse ni la
      // barre précédente/suivante (elles bornent la position des panneaux qu'elles délimitent,
      // inchangés ici), ni la hauteur/largeur du canvas (somme des tailles inchangée).
      delta = Math.max(delta, SPLITTER_MIN_SIZE - drag.sizeA)
      delta = Math.min(delta, drag.sizeB - SPLITTER_MIN_SIZE)
      const next = [...sizesRef.current]
      next[drag.index]     = `${drag.sizeA + delta}px`
      next[drag.index + 1] = `${drag.sizeB - delta}px`
      sizesRef.current = next
      setSizes(next)
    }
    const up = () => {
      dragRef.current = null
      activeHandlersRef.current = null
      document.removeEventListener('mousemove', move)
      document.removeEventListener('mouseup', up)
    }
    activeHandlersRef.current = { move, up }
    document.addEventListener('mousemove', move)
    document.addEventListener('mouseup', up)
  }, [isRow])

  // Nettoyage si le composant est démonté pendant un drag en cours
  useEffect(() => () => {
    const active = activeHandlersRef.current
    if (!active) return
    document.removeEventListener('mousemove', active.move)
    document.removeEventListener('mouseup', active.up)
  }, [])

  return (
    <div style={{ display: 'flex', flexDirection: isRow ? 'row' : 'column', width: '100%', height: '100%', minWidth: 0, minHeight: 0 }}>
      {elements.map((field, i) => (
        <Fragment key={field.id}>
          <div
            ref={el => { paneRefs.current[i] = el }}
            // `"auto"` : même sémantique que `gridColumnWidths` (layout grid) — partage égal de
            // l'espace restant entre panneaux "auto" une fois les tailles fixes déduites
            // (`flex-grow: 1`), pas un dimensionnement au contenu (comportement CSS natif de
            // `flex-basis: auto`, qui ne correspondrait pas à l'attente ici). Un panneau "auto"
            // redimensionné manuellement (drag d'une barre adjacente) devient fixe (mesure réelle
            // en px, voir handleMouseDown) — perd son comportement "auto" une fois manipulé.
            style={sizes[i] === 'auto'
              ? { flex: '1 1 0%', minWidth: 0, minHeight: 0, overflow: 'auto' }
              : { flex: `0 0 ${sizes[i]}`, minWidth: 0, minHeight: 0, overflow: 'auto' }}
          >
            {onRenderFields({ [field.id]: field }, null)}
          </div>
          {i < elements.length - 1 && (
            <div
              onMouseDown={e => handleMouseDown(i, e)}
              className={isRow ? styles.splitterBarCol : styles.splitterBarRow}
              style={{ position: 'relative', flex: `0 0 ${SPLITTER_BAR_SIZE}px`, cursor: isRow ? 'col-resize' : 'row-resize' }}
            >
              {isRow
                ? <GripVertical size={12} className={styles.splitterGrip} />
                : <GripHorizontal size={12} className={styles.splitterGrip} />
              }
            </div>
          )}
        </Fragment>
      ))}
    </div>
  )
}

export default function CanvasField({ id, label, icon, fields, gridLayout, onRenderFields, onEvent, compoGroup, collapsible = false, collapsedOnLoad = false, fixedHeight, visible, toolbarBtns = [], splitter, splitterSizes, userData, class: cssClass, style: rawStyle }) {
  // `visible: false` (JSON) → display:none posé directement sur le div [data-tbs-root] : voir
  // ChampField.jsx pour le détail (show()/hide() de tbsGeneralApi.js ciblent ce même élément).
  const visibilityStyle = visible === false ? { display: 'none' } : undefined
  const cssStyle = toStyleObject(rawStyle)
  const compoGroupRef  = useRef(compoGroup ?? null)
  const userDataRef    = useRef(userData ?? null)
  const htmlRef        = useRef(null)
  const [collapsed, setCollapsed]       = useState(collapsedOnLoad)
  const [htmlContent, setHtmlContent]   = useState(null)
  const [title, setTitle]               = useState(label)
  const isCollapsible = collapsible && !!title

  // `splitter` ("vertical"/"horizontal") et `layout` (grid) sont mutuellement exclusifs — le
  // splitter opère sur la liste plate des éléments directs du canvas, pas sur un positionnement en
  // grille. `layout` est ignoré (avec erreur) si les deux sont présents ensemble.
  let hasSplitter = splitter === 'vertical' || splitter === 'horizontal'
  if (hasSplitter && gridLayout) {
    console.error(`[CanvasField] "${id}" : attributs "splitter" et "layout" incompatibles — "layout" ignoré`)
  }
  // Un splitter "horizontal" (panneaux empilés) redimensionne verticalement — sans `fixedHeight`,
  // le conteneur (`height: '100%'`) n'a aucune hauteur bornée à hériter (le div "corps" du canvas,
  // en flux normal, a une hauteur automatique dictée par son contenu) : `overflow: auto` sur
  // chaque panneau devient alors sans effet (rien à clipper contre), et un champ redimensionné en
  // hauteur (ex. `tbsComponents[x].setHeight(3000)`) fait grandir son panneau — et donc glisser la
  // barre suivante — au lieu de simplement faire apparaître un ascenseur. Repli sur le flux
  // vertical normal plutôt que de laisser ce comportement dégénérer silencieusement.
  if (hasSplitter && splitter === 'horizontal' && !fixedHeight) {
    console.error(`[CanvasField] "${id}" : attribut "splitter" à "horizontal" sans "fixedHeight" — hauteur non bornée, "splitter" ignoré`)
    hasSplitter = false
  }
  const splitterElements = hasSplitter ? Object.values(fields ?? {}) : null

  // Toolbar dans la barre de titre — même contrat que TableField/TreeField (showToolbar/
  // hideToolbar/addElemToolbar/...), mais événement `onFrameToolbarClick` (pas `onTblToolbarClick`)
  // et sans notion de colonnes/lignes. Rendue uniquement si une barre de titre existe (collapsible
  // ou title non vide) — sans titre, aucun emplacement pour l'afficher.
  const [toolbarVisible, setToolbarVisible] = useState(true)
  const [toolbarState, setToolbarState]     = useState(toolbarBtns)
  const toolbarStateRef = useRef(toolbarBtns)
  useLayoutEffect(() => { toolbarStateRef.current = toolbarState }, [toolbarState])
  // Même schéma que TableField.jsx (setRowsSynced/setToolbarStateSynced) : la ref doit être à jour
  // de façon synchrone avant le setState, pour que getListOfToolbarBtns() appelé juste après un
  // addElemToolbar()/removeElemToolbar()/... (même traitement métier) lise la valeur à jour.
  const setToolbarStateSynced = useCallback(updaterOrValue => {
    const next = typeof updaterOrValue === 'function' ? updaterOrValue(toolbarStateRef.current) : updaterOrValue
    toolbarStateRef.current = next
    setToolbarState(next)
  }, [])

  // Blocs de champs ajoutés dynamiquement via addElements() — voir tbsComponents[id].addElements
  // ci-dessous. Chacun porte son propre onEvent/loaders (listeners), indépendants de ceux de
  // l'écran (mêmes noms que modalDialogBox : eventCallback, dynaComboLoader, gridLoader,
  // treeLoader, gridDragDropCallback, treeDragDropCallback).
  const [dynBlocks, setDynBlocks] = useState([])
  const dynBlocksRef  = useRef([])
  const nextDynKeyRef = useRef(0)

  useEffect(() => {
    window.tbsComponents = window.tbsComponents ?? {}
    window.tbsComponents[id] = {
      type: 'canvas',
      ...createGeneralApi(id, userDataRef.current),
      getCompoGroup: () => compoGroupRef.current,
      setCompoGroup: v => { compoGroupRef.current = v },
      getHtml: () => htmlRef.current,
      setHtml: html => { htmlRef.current = html; setHtmlContent(html) },
      setTitle: t => setTitle(t),
      collapse: () => setCollapsed(true),
      expand:   () => setCollapsed(false),
      // json : même structure qu'un descriptor standard ({fields, layout?/gridLayout?}). Mêmes
      // paramètres que modalDialogBox (eventCallback, dynaComboLoader, gridLoader, treeLoader,
      // gridDragDropCallback, treeDragDropCallback), tous optionnels — repli sur les fonctions
      // globales (window.onTbsEventCallback, window.tbsDynaComboLoader...) comme le reste du
      // générateur si absents. beforeCloseCallback accepté pour cohérence de signature avec
      // modalDialogBox, mais jamais déclenché : addElements n'a pas de notion de fermeture/
      // suppression (pas de removeElements) tant que le canvas reste monté.
      // flushSync : les champs ajoutés s'enregistrent dans tbsComponents à leur montage (useEffect
      // de chaque composant). Sans flush synchrone, un traitement métier appelant
      // tbsComponents[xxx] juste après addElements() (même traitement) ne les trouverait pas
      // encore — le montage/commit React n'aurait pas encore eu lieu (même raison que
      // ChampField.setValue/ComboField).
      addElements: ({ json, eventCallback, dynaComboLoader, gridLoader, treeLoader, gridDragDropCallback, treeDragDropCallback, beforeCloseCallback } = {}) => {
        if (!json) return
        const handleEvent = (fid, eventType, ...args) =>
          safeInvoke(eventCallback || window.onTbsEventCallback, fid, eventType, ...args)
        const loaders = { dynaComboLoader, gridLoader, treeLoader, gridDragDropCallback, treeDragDropCallback }
        const next = [...dynBlocksRef.current, { key: nextDynKeyRef.current++, json, handleEvent, loaders, beforeCloseCallback }]
        dynBlocksRef.current = next
        flushSync(() => setDynBlocks(next))
      },
      // Toolbar — même contrat que TableField/TreeField (voir api-reference.md).
      showToolbar:        ()                            => setToolbarVisible(true),
      hideToolbar:        ()                            => setToolbarVisible(false),
      addElemToolbar:     (btnId, icon, tip, vis, act) => setToolbarStateSynced(s => [...s, { id: btnId, icon, tooltip: tip, visible: vis, active: act }]),
      removeElemToolbar:  btnId                         => setToolbarStateSynced(s => s.filter(b => b.id !== btnId)),
      disableElemToolbar: btnId                         => setToolbarStateSynced(s => s.map(b => b.id === btnId ? { ...b, active: false } : b)),
      enableElemToolbar:  btnId                         => setToolbarStateSynced(s => s.map(b => b.id === btnId ? { ...b, active: true }  : b)),
      showElemToolbar:    btnId                         => setToolbarStateSynced(s => s.map(b => b.id === btnId ? { ...b, visible: true }  : b)),
      hideElemToolbar:    btnId                         => setToolbarStateSynced(s => s.map(b => b.id === btnId ? { ...b, visible: false } : b)),
      getListOfToolbarBtns: () => toolbarStateRef.current.map(b => b.id),
    }
    return () => { delete window.tbsComponents[id] }
  }, [id]) // eslint-disable-line react-hooks/exhaustive-deps

  const dynContent = dynBlocks.map(b => (
    <Fragment key={b.key}>{onRenderFields(b.json.fields, resolveLayout(b.json), b.handleEvent, b.loaders)}</Fragment>
  ))

  // Boutons de la toolbar de titre — rendue seulement à l'état déplié (jamais replié, même
  // principe que TableField/TreeField). `stopPropagation` : la barre repliable elle-même porte un
  // onClick (toggle collapse) sur tout son conteneur ; sans cela, cliquer un bouton toggerait
  // aussi le repli/dépli en plus de déclencher l'action du bouton.
  const toolbarButtons = toolbarVisible && toolbarState.length > 0 && (
    <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
      {toolbarState.map(btn => (
        <Button
          key={btn.id}
          size="sm"
          variant="ghost"
          title={btn.tooltip}
          disabled={btn.active === false}
          // `size="sm"` (h-8) dépasse la hauteur naturelle du titre (py-2 + text-sm) — h-6 la
          // ramène à l'intérieur de la barre au lieu de l'agrandir.
          className="tbs-toolbar-btn-size h-6 px-2"
          style={{ visibility: btn.visible === false ? 'hidden' : 'visible' }}
          onClick={btn.active !== false ? () => onEvent(id, 'onFrameToolbarClick', 0, btn.id) : undefined}
        >
          {btn.icon && <i className={btn.icon} aria-hidden="true" />}
        </Button>
      ))}
    </div>
  )

  if (collapsed && isCollapsible) {
    return (
      <div id={id} data-tbs-root={id} className={cn('mb-2', cssClass)} style={{ ...cssStyle, ...visibilityStyle }}>
        <div className={styles.collapseBar} onClick={() => setCollapsed(false)} style={{ cursor: 'pointer', borderRadius: '0.1rem', border: '1px solid hsl(var(--border))' }}>
          <i className="fa-solid fa-chevron-right fa-xs" style={{ color: '#7a8499', flexShrink: 0 }} aria-hidden="true" />
          {icon && <i className={icon} style={{ color: '#7a8499', flexShrink: 0, marginLeft: 2 }} aria-hidden="true" />}
          <span className={styles.collapseBarLabel}>{title}</span>
        </div>
        {/* Champs enfants toujours montés (même repliés) — mêmes principe que TabsField/
            AccordionField : sans cela, les composants enfants (et les tableaux/arbres imbriqués,
            comptabilisés dans onLoad via countAsyncFields sur le JSON brut) ne s'enregistreraient
            jamais dans window.tbsComponents tant que le canvas resterait replié. */}
        <div style={{ display: 'none' }}>
          {htmlContent !== null
            ? <div dangerouslySetInnerHTML={{ __html: htmlContent }} />
            : hasSplitter
              ? <SplitterPanes elements={splitterElements} splitter={splitter} splitterSizes={splitterSizes} onRenderFields={onRenderFields} />
              : onRenderFields(fields ?? [], gridLayout)
          }
          {dynContent}
        </div>
      </div>
    )
  }

  return (
    <div id={id} data-tbs-root={id} className={cn('mb-2 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))]', cssClass)} style={{ ...cssStyle, ...visibilityStyle }}>
      {isCollapsible ? (
        <div className={`${styles.collapseBar} ${styles.collapseBarExpanded}`} onClick={() => setCollapsed(true)} style={{ cursor: 'pointer', borderRadius: '0.1rem 0.1rem 0 0' }}>
          <i className="fa-solid fa-chevron-down fa-xs" style={{ color: '#7a8499', flexShrink: 0 }} aria-hidden="true" />
          {icon && <i className={icon} style={{ color: '#7a8499', flexShrink: 0, marginLeft: 2 }} aria-hidden="true" />}
          <span className={styles.collapseBarLabel}>{title}</span>
          {toolbarButtons}
        </div>
      ) : title && (
        // Même couleur de fond que `.collapseBar` (hsl(var(--muted)), voir fields.module.css) —
        // le titre d'un canvas doit avoir le même fond qu'il soit collapsible (replié ou non) ou non.
        <h3 className="px-4 py-2 text-sm font-semibold text-[hsl(var(--foreground))] bg-[hsl(var(--muted))] border-b border-[hsl(var(--border))] flex items-center gap-2">
          <span className="flex-1 flex items-center gap-2 min-w-0">
            {icon && <i className={`${icon} text-[hsl(var(--muted-foreground))]`} aria-hidden="true" />}
            {title}
          </span>
          {toolbarButtons}
        </h3>
      )}
      <div className="p-2" style={fixedHeight ? { height: toCssLength(fixedHeight), overflowY: 'auto' } : undefined}>
        {htmlContent !== null
          ? <div dangerouslySetInnerHTML={{ __html: htmlContent }} />
          : hasSplitter
            ? <SplitterPanes elements={splitterElements} splitter={splitter} splitterSizes={splitterSizes} onRenderFields={onRenderFields} />
            : onRenderFields(fields ?? [], gridLayout)
        }
        {dynContent}
      </div>
    </div>
  )
}
